/**
 * סוגי תפקידים במערכת
 * מוגדרים לפי הטבלה Roles במסד הנתונים
 * קובץ משותף לשרת ול-web
 */

export const ROLES = {
  TEACHER: 1,
  STUDENT: 2,
  ADMIN: 3,
  PRINCIPAL: 4, // מנהל בית ספר
} as const;

export const ROLE_NAMES = {
  [ROLES.TEACHER]: 'מורה',
  [ROLES.STUDENT]: 'תלמיד',
  [ROLES.ADMIN]: 'מנהל מערכת',
  [ROLES.PRINCIPAL]: 'מנהל בית ספר',
} as const;

export const ROLE_NAMES_EN = {
  [ROLES.TEACHER]: 'Teacher',
  [ROLES.STUDENT]: 'Student',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.PRINCIPAL]: 'Principal',
} as const;

/**
 * קבוצות תפקידים לבדיקות הרשאות
 */
export const ROLE_GROUPS = {
  EDUCATORS: [ROLES.TEACHER, ROLES.ADMIN, ROLES.PRINCIPAL],
  ADMINISTRATORS: [ROLES.ADMIN, ROLES.PRINCIPAL],
  STUDENTS: [ROLES.STUDENT],
  ALL: [ROLES.TEACHER, ROLES.STUDENT, ROLES.ADMIN, ROLES.PRINCIPAL],
} as const;

/**
 * הרשאות לפי פעולות
 */
export const PERMISSIONS = {
  EDIT_PROFILE: [ROLES.TEACHER, ROLES.ADMIN, ROLES.PRINCIPAL],
  ACCESS_ADMIN_PANEL: [ROLES.ADMIN, ROLES.PRINCIPAL],
  MANAGE_CLASSES: [ROLES.TEACHER, ROLES.ADMIN, ROLES.PRINCIPAL],
  VIEW_GRADES: [ROLES.STUDENT, ROLES.TEACHER, ROLES.ADMIN, ROLES.PRINCIPAL],
  MANAGE_USERS: [ROLES.ADMIN],
  MANAGE_SCHOOLS: [ROLES.ADMIN, ROLES.PRINCIPAL],
} as const;

/**
 * פונקציה לקבלת שם התפקיד בעברית
 */
export function getRoleName(roleId: number): string {
  return ROLE_NAMES[roleId as keyof typeof ROLE_NAMES] || 'לא ידוע';
}

/**
 * פונקציה לקבלת שם התפקיד באנגלית
 */
export function getRoleNameEn(roleId: number): string {
  return ROLE_NAMES_EN[roleId as keyof typeof ROLE_NAMES_EN] || 'Unknown';
}

/**
 * פונקציה לבדיקה אם משתמש הוא מורה
 */
export function isTeacher(roleId: number): boolean {
  return roleId === ROLES.TEACHER;
}

/**
 * פונקציה לבדיקה אם משתמש הוא תלמיד
 */
export function isStudent(roleId: number): boolean {
  return roleId === ROLES.STUDENT;
}

/**
 * פונקציה לבדיקה אם משתמש הוא מנהל
 */
export function isAdmin(roleId: number): boolean {
  return roleId === ROLES.ADMIN;
}

/**
 * פונקציה לבדיקה אם משתמש הוא מנהל בית ספר
 */
export function isPrincipal(roleId: number): boolean {
  return roleId === ROLES.PRINCIPAL;
}

/**
 * פונקציה לבדיקה אם משתמש הוא מורה או מנהל
 */
export function isTeacherOrAdmin(roleId: number): boolean {
  return roleId === ROLES.TEACHER || roleId === ROLES.ADMIN;
}

/**
 * פונקציה לבדיקה אם משתמש הוא מנהל או מנהל בית ספר
 */
export function isAdminOrPrincipal(roleId: number): boolean {
  return roleId === ROLES.ADMIN || roleId === ROLES.PRINCIPAL;
}

/**
 * בדיקה אם תפקיד שייך לקבוצה
 */
export function hasRole(userRoleId: number, allowedRoles: number[]): boolean {
  return allowedRoles.includes(userRoleId);
}

/**
 * בדיקה אם משתמש יכול לבצע פעולה מסוימת
 */
export function canPerformAction(userRoleId: number | undefined, action: keyof typeof PERMISSIONS): boolean {
  if (!userRoleId) return false;
  return PERMISSIONS[action].includes(userRoleId as any);
}


/**
 * מערך של כל סוגי התפקידים
 */
export const ALL_ROLES = [
  { id: ROLES.TEACHER, name_he: ROLE_NAMES[ROLES.TEACHER], name_en: ROLE_NAMES_EN[ROLES.TEACHER] },
  { id: ROLES.STUDENT, name_he: ROLE_NAMES[ROLES.STUDENT], name_en: ROLE_NAMES_EN[ROLES.STUDENT] },
  { id: ROLES.ADMIN, name_he: ROLE_NAMES[ROLES.ADMIN], name_en: ROLE_NAMES_EN[ROLES.ADMIN] },
  { id: ROLES.PRINCIPAL, name_he: ROLE_NAMES[ROLES.PRINCIPAL], name_en: ROLE_NAMES_EN[ROLES.PRINCIPAL] },
];

/**
 * טיפוסים
 */
export type RoleId = typeof ROLES[keyof typeof ROLES];
export type RoleName = typeof ROLE_NAMES[keyof typeof ROLE_NAMES];
export type RoleNameEn = typeof ROLE_NAMES_EN[keyof typeof ROLE_NAMES_EN];
